<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.addCommodity')"
            size="large">
            <div
                v-if="checkPermission(layoutPermissions.partner_commodity_add)"
                class="mb-3 text-right">
                <hts-form-button
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="openAddCommodity">
                    {{ $t('labels.add') }}
                </hts-form-button>
            </div>
            <hts-data-table
                ref="dataTables"
                store-name="cooperatorDataTable"
                store-key="CooperatorCommodity"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection
                :headers="cooperatorCommodity"
                :server="server"
                :detail-row-auto-height="true" />
            <add-commodity
                ref="addCommodity"
                @refresh-tables="refreshTables" />
            <hts-modal
                :show="showPopupDeleteCommodity"
                :title="$t('labels.deleteCommodity')"
                type="confirm"
                :content="$t('labels.confirmDeleteCommodity')"
                @hide="closePopupDeleteCommodity"
                @confirm="handleDeleteCommodity" />
        </hts-router-drawer>
        <router-view @refresh-tables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddCommodity from './commodity/-components/add-commodity.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { IDataCommodity } from '~/pages/trucker-[id]/partner/-components/imports/form'

import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'

const cooperatorCode = computed(() => useRoute().params.idpartner)

const { $formatters } = useNuxtApp()
const showPopupDeleteCommodity = ref(false)
const openAddCommodity = () => {
  if (addCommodity.value) {
    addCommodity.value.open(cooperatorCode.value)
  }
}

const openEditCommodity = (commodity: IDataCommodity) => {
  useRouter().push({ name: 'trucker-id-partner-idpartner-commodity-idcommodity-edit', params: { idcommodity: commodity._id } })
}

const openDeleteCommodity = (commodity: IDataCommodity) => {
  commodityId.value = commodity._id
  showPopupDeleteCommodity.value = true
}

const cooperatorCommodity = [
  {
    field: 'category',
    headerName: $t('labels.category')
  },
  {
    field: 'commodity',
    headerName: $t('labels.commodity'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      storeKey: 'cooperatorDataTable',
      storeName: 'CooperatorCommodity',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_commodity_edit),
          click: openEditCommodity
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.partner_commodity_delete),
          click: openDeleteCommodity,
          isArchive: true
        }
      ]
    }
  }
]
const visible = ref(false)
const server = {
  method: 'post',
  url: '/partner/commodity/by-cooperator/' + cooperatorCode.value
}
const commodityId = ref('')
const dataTables = ref()
const addCommodity: any = ref(null)

const props = defineProps({})

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const closePopupDeleteCommodity = () => {
  showPopupDeleteCommodity.value = false
  commodityId.value = ''
}

const handleDeleteCommodity = () => {
  const api = {
    url: '/partner/commodity/cooperatorID/delete',
    method: 'delete'
  }
  $api(api, { uriParams: { cooperatorID: cooperatorCode.value }, body: { ids: [commodityId.value] } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteCommoditySuccess'))
      }
      refreshTables()
      showPopupDeleteCommodity.value = false
    })
    .catch(() => {
      showPopupDeleteCommodity.value = false
    })
}

const close = () => {
  visible.value = false
}
const useArchive = { api: $url.partner.commodity.archive, permissions: checkPermission(layoutPermissions.partner_commodity_archive) }
const useRestore = { api: $url.partner.commodity.restore, permissions: checkPermission(layoutPermissions.partner_commodity_restore) }
</script>
